import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface MegaShopProps {
  lang: string
  onItemClick: () => void
}

// Six-column shop taxonomy modelled on the ZENZ reference. Each item links
// to /shop with a search query that filters the catalogue by keyword
// (matched case-insensitively against product title + brand by
// `applyFilters`). When Dion ships Shopify collection handles per
// SHOP_CATALOG_CHECKLIST.md, swap the `q` field for `collection` and
// consume `?collection=<handle>` in ShopPage.
//
// `q` is the substring used for filtering. When absent, the item `key` is
// used directly - works for keys that already read as a category word
// ('shampoo', 'foundation'). Override `q` for camelCased keys whose
// verbatim form would never match a real product title ('eyesBrows' ->
// 'brow') or for short anchor-words that bring in the long-tail.
const GROUPS: Array<{ titleKey: string; items: Array<{ key: string; q?: string }> }> = [
  {
    titleKey: 'megaShop.groupHairCare',
    items: [
      { key: 'shampoo' },
      { key: 'conditioner' },
      { key: 'masksOils', q: 'mask' },
      { key: 'styling' },
    ],
  },
  {
    titleKey: 'megaShop.groupSkinCare',
    items: [
      { key: 'naturalSkincare', q: 'skincare' },
      { key: 'face' },
      { key: 'body' },
      { key: 'hands', q: 'hand' },
    ],
  },
  {
    titleKey: 'megaShop.groupMakeup',
    items: [
      { key: 'foundation' },
      { key: 'cheeks', q: 'cheek' },
      { key: 'eyesBrows', q: 'brow' },
      { key: 'lips', q: 'lip' },
    ],
  },
  {
    titleKey: 'megaShop.groupMen',
    items: [
      { key: 'hairCare', q: 'men' },
      { key: 'menStyling', q: 'styling' },
      { key: 'beard' },
      { key: 'menFace', q: 'face' },
    ],
  },
  {
    titleKey: 'megaShop.groupSignature',
    items: [
      { key: 'growth' },
      { key: 'pure' },
      { key: 'sweetSense', q: 'sweet' },
      { key: 'cactus' },
    ],
  },
  {
    titleKey: 'megaShop.groupRoutines',
    items: [
      { key: 'hair' },
      { key: 'skin' },
    ],
  },
]

/** Builds `/{lang}/shop?q=<encoded>` for a mega-menu item. Internal - not
 *  exported because react-refresh requires component files to export only
 *  components. If reused elsewhere, lift into a `MegaShop.utils.ts` sibling. */
function buildShopItemHref(lang: string, item: { key: string; q?: string }): string {
  const q = (item.q ?? item.key).toLowerCase()
  return `/${lang}/shop?q=${encodeURIComponent(q)}`
}

export default function MegaShop({ lang, onItemClick }: MegaShopProps) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-12 gap-6">
      {GROUPS.map((group) => (
        <div key={group.titleKey} className="col-span-6 sm:col-span-4 md:col-span-2">
          <h3 className="font-body font-semibold text-[10px] tracking-[0.25em] text-gold uppercase mb-4 pb-2 border-b border-gold/20">
            {t(group.titleKey)}
          </h3>
          <ul className="space-y-2.5">
            {group.items.map((item, i) => (
              <li key={`${group.titleKey}-${item.key}-${i}`}>
                <Link
                  to={buildShopItemHref(lang, item)}
                  onClick={onItemClick}
                  className="block font-body text-sm text-mocha/80 hover:text-gold transition-colors"
                >
                  {t(`megaShop.items.${item.key}`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
